/**
 * @fileoverview General utilities for text management.
 */

import getContext from './get-context'
import { ensureArray } from 'lib/array'

const CACHE = {}
const DEFAULT_FONT = '14px sans-serif'

/**
 * Given text and a font, calculate the pixel width
 * @param {string} text
 * @param {string} [font]
 */
export function measureText(text, font = DEFAULT_FONT) {
  const key = font + text

  if (CACHE.hasOwnProperty(key)) {
    return CACHE[key]
  }

  const ctx = getContext()

  ctx.font = font

  CACHE[key] = ctx.measureText(text).width

  return CACHE[key]
}

/**
 * Calculate the required gap for an axis based on labels
 * @param {Array.<number|string>} values
 * @param {Function} [format]
 * @param {string} [font]
 * @returns {number} Longest measured value in pixels
 */
export function textGap(values, format = n => n, font = DEFAULT_FONT) {
  return ensureArray(values).reduce(function(widest, value) {
    let next = measureText(format(value), font)

    return Math.max(widest, next)
  }, 0)
}

/**
 * Calculate the required gap for an axis based on labels
 * @param {Object} options
 * @param {string} options.text
 * @param {string} options.font
 * @param {number} options.lineHeight
 * @param {number} options.width
 * @returns {Object} { lines, width, height }
 */
export function wrappedTextGap({ text, font, lineHeight = 20, width = 100 }) {
  let key = [text, font, lineHeight, width].join('-')

  if (CACHE.hasOwnProperty(key)) {
    return CACHE[key]
  }

  const words = `${text}`.split(' ')
  const lines = []

  let line = ''

  // For each word...
  for (var i = 0, len = words.length; i <= len; i++) {
    // Add a word to the current line
    var next = `${line} ${words[i]}`.trim()

    // If the next line is too long...
    if (i === len || measureText(next, font) >= width) {
      // Push the current line and start over
      if (line.length) {
        lines.push(line)
      }

      line = words[i]
    } else {
      // Otherwise keep tracking the current line
      line = next
    }
  }

  CACHE[key] = {
    lines,
    width,
    height: lines.length * lineHeight
  }

  return CACHE[key]
}



// WEBPACK FOOTER //
// /home/runner/work/web-interactive/web-interactive/lib/text/index.js